import {
  Box,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useOutletContext } from "react-router";
import { useApi } from "@platform/api";
import { Alert, Typography } from "@platform/design-system";

import { ConfirmDialog, Loader } from "../../../components";
import { extractErrorMessage } from "../../../utils";
import { ConnectedCarriersContext } from "../types";

type Carrier = {
  carrierId: number | string;
  carrierName: string;
  enabled: boolean;
};

type CarrierRowProps = {
  item: Carrier;
  onSelect: (item: Carrier) => void;
};

const ConnectedCarriers = (): React.JSX.Element => {
  const { brokerId, brokerName } = useOutletContext<ConnectedCarriersContext>();
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const connectedCarriersQueryKey = ["connectedCarriers", brokerId];
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery<Carrier[]>({
    queryKey: connectedCarriersQueryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.post<Carrier[]>(
        "/brokerportal/v1/listBrokerConnectedCarriers",
        { brokerId },
      );
      return data ?? [];
    },
  });
  const carriers = data ?? [];

  const {
    mutate: updateCarrier,
    isPending: isUpdating,
    error: mutationError,
    reset: resetMutation,
  } = useMutation<void, Error, { item: Carrier; checked: boolean }>({
    mutationFn: async ({ item, checked }) =>
      axiosInstance.post("/brokerportal/v1/updateBrokerConnectedCarrier", {
        brokerId,
        carrierId: item.carrierId,
        enabled: checked,
      }),
    onMutate: async ({ item, checked }) => {
      await queryClient.cancelQueries({
        queryKey: connectedCarriersQueryKey,
      });

      queryClient.setQueryData<Carrier[]>(
        connectedCarriersQueryKey,
        (old = []) =>
          old.map((elem) =>
            elem.carrierId === item.carrierId
              ? { ...elem, enabled: checked }
              : elem,
          ),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: connectedCarriersQueryKey,
      });
    },
  });

  const handleCheck = (checked: boolean, item: Carrier): void => {
    handleCloseModal();
    updateCarrier({ item, checked });
  };

  function handleCloseModal(): void {
    setSelectedCarrier(null);
  }

  if (isLoading || isUpdating) {
    return <Loader />;
  }

  const error = queryError || mutationError;

  const CarrierRow: React.FC<CarrierRowProps> = ({ item, onSelect }) => (
    <TableRow key={item.carrierId}>
      <TableCell>{item.carrierName}</TableCell>
      <TableCell>
        <Switch
          checked={item.enabled}
          onChange={() => onSelect(item)}
          slotProps={{
            input: {
              "aria-label": `switch-${item.carrierName}`,
            },
          }}
        />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      {error && (
        <Box mt={2} mb={2}>
          <Alert
            severity="error"
            onClose={mutationError ? resetMutation : undefined}
          >
            {extractErrorMessage(error)}
          </Alert>
        </Box>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Carrier Name</TableCell>
              <TableCell>Connected</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carriers.length ? (
              carriers
                .filter((item) => item.carrierName)
                .map((item) => (
                  <CarrierRow
                    key={item.carrierId}
                    item={item}
                    onSelect={setSelectedCarrier}
                  />
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>No connected carriers found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedCarrier && (
        <ConfirmDialog
          isOpen={!!selectedCarrier}
          title={`Turn ${selectedCarrier.enabled ? "Off" : "On"} Policy Connection`}
          message={
            <Typography component="span">
              This will immediately {selectedCarrier.enabled ? "remove" : "add"}{" "}
              the View Policy Details feature for all <b>{brokerName}</b> client
              policies with this carrier.
            </Typography>
          }
          subMessage={
            <Typography component="span">
              Are you sure you want to turn{" "}
              {selectedCarrier.enabled ? "Off" : "On"} this feature?
            </Typography>
          }
          confirmBtnMsg={`YES, TURN ${selectedCarrier.enabled ? "OFF" : "ON"}`}
          onClose={handleCloseModal}
          closeBtnMsg="NO, CANCEL"
          onConfirm={() => {
            handleCheck(!selectedCarrier.enabled, selectedCarrier);
          }}
        />
      )}
    </>
  );
};

export { ConnectedCarriers };
